import type { SupportedGames } from "@repo/game-configuration"
import type { Locale } from "@repo/i18n"
import type {
	AdminIntegrationApp,
	IntegrationAppScreenshot,
	IntegrationScope,
} from "@repo/marketplace-admin"
import {
	type IntegrationAppCategory,
	MAX_INTEGRATION_APP_CATEGORIES,
	MCP_APP_SLUG,
} from "@repo/shared-primitives"
import {
	createFileRoute,
	Link,
	useNavigate,
	useParams,
} from "@tanstack/react-router"
import {
	AlertTriangle,
	AppWindow,
	ArrowLeft,
	ExternalLink,
	FileDiff,
	Gauge,
	KeyRound,
	LayoutGrid,
	ShieldAlert,
	ShieldCheck,
	UserRound,
} from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import {
	DetailPanel,
	DetailPanelSection,
	PageHeader,
} from "@/components/layout"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot } from "@/components/ui/status"
import { Switch } from "@/components/ui/switch"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { AppPriceDataSection } from "@/features/api-data-access/AppPriceDataSection"
import { AppUsageTab } from "@/features/api-usage/AppUsageTab"
import { AppAudienceFields } from "@/features/integration-apps/AppAudienceFields"
import { CategoryPicker } from "@/features/integration-apps/CategoryPicker"
import { CredentialsTable } from "@/features/integration-apps/CredentialsTable"
import { IntegrationGrantsGrid } from "@/features/integration-apps/IntegrationGrantsGrid"
import {
	cleanLocalized,
	LocalizedDescriptionEditor,
} from "@/features/integration-apps/LocalizedDescriptionEditor"
import { LogoUploadField } from "@/features/integration-apps/LogoUploadField"
import { OAuthBlockedHostsSection } from "@/features/integration-apps/OAuthBlockedHostsSection"
import { RedirectUriEditor } from "@/features/integration-apps/RedirectUriEditor"
import {
	isIntegrationScope,
	ScopePicker,
} from "@/features/integration-apps/ScopePicker"
import { ScreenshotsField } from "@/features/integration-apps/ScreenshotsField"
import { SettingRow, SettingRows } from "@/features/integration-apps/SettingRow"
import { ShowOnceTokenDialog } from "@/features/integration-apps/ShowOnceTokenDialog"
import { createTabSearch } from "@/hooks/use-url-tab"
import {
	useCreateIntegrationCredential,
	useIntegrationApp,
	useIntegrationGrants,
	useIntegrationRateLimits,
	useIntegrationWebhookPortal,
	useRateLimitBuckets,
	useRevokeAllIntegrationGrants,
	useRevokeIntegrationCredential,
	useSetIntegrationAppOwner,
	useSetIntegrationAppStatus,
	useSetIntegrationRateLimits,
	useUpdateIntegrationApp,
	useUpdateIntegrationAppPrivileges,
} from "@/queries/marketplace"

const TABS = [
	"settings",
	"credentials",
	"connections",
	"usage",
	"rate-limits",
	"managed",
] as const
type AppTab = (typeof TABS)[number]

/**
 * Setting rows inside a detail panel section: hairlines between rows only,
 * the section's padding standing in for the first and last row's.
 */
function PanelSettingRows({ children }: { children: React.ReactNode }) {
	return (
		<SettingRows className="-my-3.5 border-t-0 [&>*:last-child]:border-b-0">
			{children}
		</SettingRows>
	)
}

export const Route = createFileRoute("/integration-apps/$appId/")({
	component: IntegrationAppDetailPage,
	validateSearch: createTabSearch(TABS),
})

/** An integration app's admin page: settings, credentials, grants, usage and privileges tabs. */
function IntegrationAppDetailPage() {
	const { appId } = useParams({ from: "/integration-apps/$appId/" })
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const { isSuperadmin } = useAdminRolesContext()
	const {
		app,
		credentials,
		pendingListingProposalId,
		pendingScopeRequestId,
		isLoading,
		error,
	} = useIntegrationApp(appId)
	// Just the count for the tab label + the revoke-all copy; the grid loads its
	// own pages server-side.
	const { total: grantTotal } = useIntegrationGrants(appId, 1)

	const setStatus = useSetIntegrationAppStatus()
	const createCredential = useCreateIntegrationCredential()
	const revokeCredential = useRevokeIntegrationCredential()
	const revokeAllGrants = useRevokeAllIntegrationGrants()
	const webhookPortal = useIntegrationWebhookPortal()

	const [mintedToken, setMintedToken] = React.useState<string | null>(null)
	const [credentialLabel, setCredentialLabel] = React.useState("")
	const [enableConfirmOpen, setEnableConfirmOpen] = React.useState(false)
	const [revokeAllConfirmOpen, setRevokeAllConfirmOpen] = React.useState(false)

	const requestedTab = searchParams.tab ?? "settings"
	const activeTab =
		requestedTab === "managed" && !isSuperadmin ? "settings" : requestedTab

	const setActiveTab = (tab: string) => {
		navigate({
			to: "/integration-apps/$appId",
			params: { appId },
			search: tab === "settings" ? {} : { tab: tab as AppTab },
			replace: true,
		})
	}

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<Skeleton className="h-10 w-64 max-w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}

	if (error || !app) {
		return (
			<div className="p-4 sm:p-6">
				<div className="rounded-lg bg-bad-soft px-4 py-3 text-bad">
					{error?.message ?? "Integration app not found"}
				</div>
			</div>
		)
	}

	const liveCredentialCount = credentials.filter((c) => !c.revokedAt).length

	function mintCredential() {
		const label = credentialLabel.trim() || "rotation"
		createCredential.mutate(
			{ appId, label },
			{
				onSuccess: (result) => {
					setCredentialLabel("")
					setMintedToken(result.plaintextToken)
				},
			},
		)
	}

	function openWebhookPortal() {
		webhookPortal.mutate(
			{ appId },
			{
				onSuccess: (result) => {
					window.open(result.portalUrl, "_blank", "noopener,noreferrer")
				},
			},
		)
	}

	return (
		<div className="flex flex-col gap-6 p-4 sm:p-6">
			<div className="flex flex-col gap-3">
				<Link
					to="/integration-apps"
					className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
				>
					<ArrowLeft className="h-3.5 w-3.5" /> Integration apps
				</Link>
				<PageHeader
					title={
						<span className="inline-flex items-center gap-3">
							{app.logoUrl && (
								<img src={app.logoUrl} alt="" className="size-7 rounded" />
							)}
							{app.name}
						</span>
					}
					description={
						<span className="flex flex-wrap items-center gap-x-3 gap-y-1">
							<span className="font-mono text-foreground text-xs">
								{app.slug}
							</span>
							<StatusDot tone={app.status === "enabled" ? "ok" : "neutral"}>
								{app.status === "enabled" ? "Enabled" : "Disabled"}
							</StatusDot>
							{app.isDevelopment && <Badge variant="info">Development</Badge>}
						</span>
					}
					actions={
						<>
							<Button
								variant="outline"
								disabled={webhookPortal.isPending}
								onClick={openWebhookPortal}
							>
								<ExternalLink className="h-4 w-4" />
								Webhook portal
							</Button>
							{app.status === "enabled" ? (
								<Button
									variant="outline"
									disabled={setStatus.isPending}
									onClick={() =>
										setStatus.mutate({ appId, status: "disabled" })
									}
								>
									Disable
								</Button>
							) : (
								<Button
									disabled={setStatus.isPending || !isSuperadmin}
									title={
										isSuperadmin
											? undefined
											: "Only a superadmin can put an app live"
									}
									onClick={() => setEnableConfirmOpen(true)}
								>
									Enable
								</Button>
							)}
						</>
					}
				/>
			</div>

			{pendingListingProposalId && (
				<div className="flex items-center justify-between gap-4 rounded-lg bg-warn-soft px-4 py-2.5">
					<div className="flex items-center gap-3">
						<FileDiff className="h-4 w-4 shrink-0 text-warn" />
						<p>The owner proposed changes to this listing.</p>
					</div>
					<Button size="sm" variant="outline" asChild>
						<Link
							to="/integration-apps/listing-requests/$proposalId"
							params={{ proposalId: pendingListingProposalId }}
						>
							Review
						</Link>
					</Button>
				</div>
			)}

			{pendingScopeRequestId && (
				<div className="flex items-center justify-between gap-4 rounded-lg bg-warn-soft px-4 py-2.5">
					<div className="flex items-center gap-3">
						<KeyRound className="h-4 w-4 shrink-0 text-warn" />
						<p>The owner requested a change of permissions.</p>
					</div>
					<Button size="sm" variant="outline" asChild>
						<Link
							to="/integration-apps/permission-requests/$requestId"
							params={{ requestId: pendingScopeRequestId }}
						>
							Review
						</Link>
					</Button>
				</div>
			)}

			<Tabs value={activeTab} onValueChange={setActiveTab} className="gap-6">
				<TabsList variant="line">
					<TabsTrigger value="settings">Settings</TabsTrigger>
					<TabsTrigger value="credentials">
						Credentials ({liveCredentialCount})
					</TabsTrigger>
					<TabsTrigger value="connections">
						Connections ({grantTotal})
					</TabsTrigger>
					<TabsTrigger value="usage">Usage</TabsTrigger>
					<TabsTrigger value="rate-limits">Rate limits</TabsTrigger>
					{isSuperadmin && (
						<TabsTrigger value="managed">Privileges</TabsTrigger>
					)}
				</TabsList>

				<TabsContent value="usage" className="mt-0">
					<AppUsageTab appId={app.id} />
				</TabsContent>

				<TabsContent value="settings" className="mt-0">
					<SettingsTab app={app} />
				</TabsContent>

				<TabsContent value="credentials" className="mt-0">
					<DetailPanel
						title="Credentials"
						icon={KeyRound}
						badges={
							<span className="text-xs text-meta tabular-nums">
								{liveCredentialCount}
							</span>
						}
					>
						<DetailPanelSection label="Mint a credential">
							<div className="flex flex-col gap-1.5">
								<div className="flex items-center gap-2">
									<Input
										id="credentialLabel"
										aria-label="Credential label"
										value={credentialLabel}
										onChange={(event) => setCredentialLabel(event.target.value)}
										placeholder="rotation-2026-q3"
										className="max-w-sm"
									/>
									<Button
										disabled={createCredential.isPending}
										onClick={mintCredential}
									>
										Mint
									</Button>
								</div>
								<p className="text-xs text-meta">
									Two credentials may be live at once so a rotation can overlap.
									Each expires 12 months after minting.
								</p>
							</div>
						</DetailPanelSection>
						<CredentialsTable
							credentials={credentials}
							isRevoking={revokeCredential.isPending}
							onRevoke={(credentialId) =>
								revokeCredential.mutate({ appId, credentialId })
							}
						/>
					</DetailPanel>
				</TabsContent>

				<TabsContent value="connections" className="mt-0">
					<div className="flex flex-col gap-4">
						<IntegrationGrantsGrid appId={appId} />

						{app.slug === MCP_APP_SLUG && <OAuthBlockedHostsSection />}

						{isSuperadmin && (
							<DetailPanel title="Compromise response" icon={ShieldAlert}>
								<DetailPanelSection>
									<PanelSettingRows>
										<SettingRow
											label={
												<span className="inline-flex items-center gap-2">
													<AlertTriangle className="h-4 w-4 text-bad" />
													Revoke all connections
												</span>
											}
											help="Every connected account loses the app immediately and must re-approve. Grants are append-only, so this cannot be undone."
										>
											<Button
												variant="destructive"
												className="w-fit"
												disabled={revokeAllGrants.isPending}
												onClick={() => setRevokeAllConfirmOpen(true)}
											>
												Revoke all
											</Button>
										</SettingRow>
									</PanelSettingRows>
								</DetailPanelSection>
							</DetailPanel>
						)}
					</div>
				</TabsContent>

				<TabsContent value="rate-limits" className="mt-0">
					<RateLimitsTab appId={appId} />
				</TabsContent>

				{isSuperadmin && (
					<TabsContent value="managed" className="mt-0">
						<PrivilegesTab app={app} />
					</TabsContent>
				)}
			</Tabs>

			<ShowOnceTokenDialog
				token={mintedToken}
				onClose={() => setMintedToken(null)}
			/>

			<AlertDialog open={enableConfirmOpen} onOpenChange={setEnableConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Put {app.name} live?</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="flex flex-col gap-2 text-sm">
								<p>
									Its credentials start authenticating immediately and users can
									reach the consent screen at /connect/{app.slug}.
								</p>
								<div className="rounded-md bg-muted p-3">
									<div className="font-medium">{app.name}</div>
									<div className="mt-1 font-mono text-xs">
										{app.scopes.join(", ")}
									</div>
									<div className="mt-2 font-mono text-xs">
										{app.redirectUris.length > 0
											? app.redirectUris.join("\n")
											: "No redirect URIs registered"}
									</div>
								</div>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => setStatus.mutate({ appId, status: "enabled" })}
						>
							Enable
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={revokeAllConfirmOpen}
				onOpenChange={setRevokeAllConfirmOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Revoke every connection to {app.name}?
						</AlertDialogTitle>
						<AlertDialogDescription>
							{grantTotal} connection(s) will be cut. Each affected user has to
							go through the consent screen again to restore access.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => revokeAllGrants.mutate({ appId })}
						>
							Revoke all
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}

interface AppTabProps {
	app: AdminIntegrationApp
}

/** Editable app metadata: identity, descriptions, gallery, directory listing, scopes and redirect URIs. */
function SettingsTab({ app }: AppTabProps) {
	const updateApp = useUpdateIntegrationApp()

	const [name, setName] = React.useState(app.name)
	const [shortDescription, setShortDescription] = React.useState<
		Record<string, string>
	>(app.shortDescription)
	const [longDescription, setLongDescription] = React.useState<
		Record<string, string>
	>(app.longDescription)
	const [logoUrl, setLogoUrl] = React.useState<string | null>(app.logoUrl)
	const [websiteUrl, setWebsiteUrl] = React.useState(app.websiteUrl ?? "")
	const [scopes, setScopes] = React.useState<IntegrationScope[]>(
		app.scopes.filter(isIntegrationScope),
	)
	const [redirectUris, setRedirectUris] = React.useState(app.redirectUris)
	const [isDevelopment, setIsDevelopment] = React.useState(app.isDevelopment)
	const [isPublic, setIsPublic] = React.useState(app.isPublic)
	const [sortOrder, setSortOrder] = React.useState(String(app.sortOrder))
	const [isFeatured, setIsFeatured] = React.useState(app.isFeatured)
	const [categories, setCategories] = React.useState<IntegrationAppCategory[]>(
		app.categories,
	)
	const [developerName, setDeveloperName] = React.useState(
		app.developerName ?? "",
	)
	const [supportUrl, setSupportUrl] = React.useState(app.supportUrl ?? "")
	const [privacyPolicyUrl, setPrivacyPolicyUrl] = React.useState(
		app.privacyPolicyUrl ?? "",
	)
	const [termsUrl, setTermsUrl] = React.useState(app.termsUrl ?? "")
	const [screenshots, setScreenshots] = React.useState<
		IntegrationAppScreenshot[]
	>(app.screenshots)
	const [games, setGames] = React.useState<SupportedGames[]>(app.games)
	const [languages, setLanguages] = React.useState<Locale[]>(app.languages)
	const [allowedIpCidrs, setAllowedIpCidrs] = React.useState(
		app.allowedIpCidrs.join(", "),
	)

	function save() {
		updateApp.mutate({
			appId: app.id,
			patch: {
				name: name.trim(),
				shortDescription: cleanLocalized(shortDescription),
				longDescription: cleanLocalized(longDescription),
				logoUrl,
				websiteUrl: websiteUrl.trim() || null,
				scopes,
				redirectUris,
				isDevelopment,
				isPublic,
				sortOrder: Number.parseInt(sortOrder, 10) || 0,
				isFeatured,
				categories,
				developerName: developerName.trim() || null,
				supportUrl: supportUrl.trim() || null,
				privacyPolicyUrl: privacyPolicyUrl.trim() || null,
				termsUrl: termsUrl.trim() || null,
				screenshots: screenshots.map((screenshot) => ({
					url: screenshot.url,
					caption: screenshot.caption?.trim() || null,
				})),
				games,
				languages,
				allowedIpCidrs: allowedIpCidrs
					.split(",")
					.map((entry) => entry.trim())
					.filter(Boolean),
			},
		})
	}

	return (
		<div className="flex flex-col gap-4">
			<OwnerCard app={app} />

			<DetailPanel title="App" icon={AppWindow}>
				<DetailPanelSection>
					<PanelSettingRows>
						<SettingRow label="Name" htmlFor="detail-name">
							<Input
								id="detail-name"
								value={name}
								onChange={(event) => setName(event.target.value)}
							/>
						</SettingRow>
						<SettingRow label="Logo">
							<LogoUploadField value={logoUrl} onChange={setLogoUrl} />
						</SettingRow>
						<SettingRow
							label="Website URL"
							htmlFor="detail-website"
							help="Where directory visitors go to set up this app's CardNexus integration."
						>
							<Input
								id="detail-website"
								value={websiteUrl}
								onChange={(event) => setWebsiteUrl(event.target.value)}
								placeholder="https://partner.example/cardnexus"
							/>
						</SettingRow>
						<SettingRow
							label="Development app"
							htmlFor="detail-development"
							help="Turning this off re-validates every redirect URI against production rules, so localhost entries must be removed first."
						>
							<Switch
								id="detail-development"
								checked={isDevelopment}
								onCheckedChange={setIsDevelopment}
							/>
						</SettingRow>
						<SettingRow
							label="Allowed source CIDRs"
							htmlFor="detail-cidrs"
							help="Comma-separated. Leave empty to accept requests from any address."
						>
							<Input
								id="detail-cidrs"
								value={allowedIpCidrs}
								placeholder="203.0.113.0/24, 198.51.100.7"
								className="font-mono text-xs"
								onChange={(event) => setAllowedIpCidrs(event.target.value)}
							/>
						</SettingRow>
					</PanelSettingRows>
				</DetailPanelSection>
			</DetailPanel>

			<DetailPanel title="Directory listing" icon={LayoutGrid}>
				<DetailPanelSection label="Descriptions">
					<p className="-mt-1 text-xs text-meta">
						The short line shows on the consent screen and directory card; the
						long Markdown shows on the directory detail page. English is the
						fallback.
					</p>
					<LocalizedDescriptionEditor
						shortDescription={shortDescription}
						longDescription={longDescription}
						onChange={(next) => {
							setShortDescription(next.shortDescription)
							setLongDescription(next.longDescription)
						}}
					/>
				</DetailPanelSection>

				<DetailPanelSection label="Screenshots">
					<ScreenshotsField value={screenshots} onChange={setScreenshots} />
				</DetailPanelSection>

				<DetailPanelSection label="Public directory">
					<PanelSettingRows>
						<SettingRow
							label="List in the directory"
							htmlFor="detail-public"
							help="Shown in the public integrations directory when on; the app must also be enabled. Development apps are listed too, and users never see that they are in development."
						>
							<Switch
								id="detail-public"
								checked={isPublic}
								onCheckedChange={setIsPublic}
							/>
						</SettingRow>
						<SettingRow
							label="Sort order"
							htmlFor="detail-sort"
							help="Lower numbers appear first in the directory."
						>
							<Input
								id="detail-sort"
								type="number"
								min={0}
								value={sortOrder}
								className="max-w-[140px] tabular-nums"
								onChange={(event) => setSortOrder(event.target.value)}
							/>
						</SettingRow>
						<SettingRow
							label="Featured"
							htmlFor="detail-featured"
							help={`Shown in the directory's "Featured by CardNexus" section.`}
						>
							<Switch
								id="detail-featured"
								checked={isFeatured}
								onCheckedChange={setIsFeatured}
							/>
						</SettingRow>
						<SettingRow
							label="Categories"
							help={`Up to ${MAX_INTEGRATION_APP_CATEGORIES}. Visitors filter the directory by these.`}
							wide
						>
							<CategoryPicker value={categories} onChange={setCategories} />
						</SettingRow>
						<AppAudienceFields
							idPrefix="detail"
							games={games}
							languages={languages}
							onGamesChange={setGames}
							onLanguagesChange={setLanguages}
							asRows
						/>
						<SettingRow
							label="Developer name"
							htmlFor="detail-developer"
							help={`Shown as "by …" on the directory and the consent screen. Leave empty to hide.`}
						>
							<Input
								id="detail-developer"
								value={developerName}
								onChange={(event) => setDeveloperName(event.target.value)}
								placeholder="Partner Ltd"
							/>
						</SettingRow>
						<SettingRow label="Support URL" htmlFor="detail-support">
							<Input
								id="detail-support"
								value={supportUrl}
								onChange={(event) => setSupportUrl(event.target.value)}
								placeholder="https://partner.example/support"
							/>
						</SettingRow>
						<SettingRow
							label="Privacy policy URL"
							htmlFor="detail-privacy"
							help="Linked from the consent screen."
						>
							<Input
								id="detail-privacy"
								value={privacyPolicyUrl}
								onChange={(event) => setPrivacyPolicyUrl(event.target.value)}
								placeholder="https://partner.example/privacy"
							/>
						</SettingRow>
						<SettingRow
							label="Terms of service URL"
							htmlFor="detail-terms"
							help="Linked from the consent screen."
						>
							<Input
								id="detail-terms"
								value={termsUrl}
								onChange={(event) => setTermsUrl(event.target.value)}
								placeholder="https://partner.example/terms"
							/>
						</SettingRow>
					</PanelSettingRows>
				</DetailPanelSection>
			</DetailPanel>

			<DetailPanel title="Access" icon={ShieldCheck}>
				<DetailPanelSection label="Scope bundle">
					<p className="-mt-1 text-xs text-meta">
						Narrowing takes effect immediately on existing connections. Widening
						does not: each user re-approves to gain the new scopes.
					</p>
					<ScopePicker
						value={scopes}
						onChange={setScopes}
						className="-mx-4 -mb-4 rounded-none border-x-0 border-b-0 bg-transparent"
					/>
				</DetailPanelSection>

				<DetailPanelSection label="Redirect URIs">
					<RedirectUriEditor value={redirectUris} onChange={setRedirectUris} />
				</DetailPanelSection>

				<AppPriceDataSection appId={app.id} appName={app.name} />
			</DetailPanel>

			<div className="flex justify-end">
				<Button disabled={updateApp.isPending} onClick={save}>
					{updateApp.isPending ? "Saving…" : "Save changes"}
				</Button>
			</div>
		</div>
	)
}

/**
 * Superadmin privileges: creating managed accounts (with its daily quota) and
 * pushing tracking events.
 */
function PrivilegesTab({ app }: AppTabProps) {
	const updatePrivileges = useUpdateIntegrationAppPrivileges()

	const [canCreate, setCanCreate] = React.useState(app.canCreateManagedAccounts)
	const [canPushTracking, setCanPushTracking] = React.useState(
		app.canPushTrackingEvents,
	)
	const [allowPublicClient, setAllowPublicClient] = React.useState(
		app.allowPublicClient,
	)
	const [quota, setQuota] = React.useState(
		String(app.managedAccountQuotaPerDay),
	)

	function save() {
		const trimmed = quota.trim()
		if (!/^\d+$/.test(trimmed)) {
			toast.error("Enter a whole non-negative daily quota")
			return
		}
		const parsed = Number.parseInt(trimmed, 10)
		updatePrivileges.mutate({
			appId: app.id,
			canCreateManagedAccounts: canCreate,
			managedAccountQuotaPerDay: parsed,
			canPushTrackingEvents: canPushTracking,
			allowPublicClient,
		})
	}

	return (
		<div className="flex flex-col gap-4">
			<DetailPanel title="Privileges" icon={ShieldCheck}>
				<DetailPanelSection>
					<PanelSettingRows>
						<SettingRow
							label="Can create managed accounts"
							htmlFor="managed-enabled"
							help="Lets the platform provision CardNexus accounts for sellers who don't have one yet. Not a scope: no API key can reach it."
						>
							<Switch
								id="managed-enabled"
								checked={canCreate}
								onCheckedChange={setCanCreate}
							/>
						</SettingRow>
						<SettingRow label="Daily creation quota" htmlFor="managed-quota">
							<Input
								id="managed-quota"
								type="number"
								min={0}
								step={1}
								value={quota}
								inputMode="numeric"
								className="max-w-[160px] tabular-nums"
								onChange={(event) => setQuota(event.target.value)}
							/>
						</SettingRow>
						<SettingRow
							label="Can push tracking events"
							htmlFor="push-tracking-enabled"
							help="Lets the platform send mail and carrier scans for the sales of sellers who connected it. Scans can mark parcels delivered, so only turn this on for label providers. Not a scope: no API key can reach it."
						>
							<Switch
								id="push-tracking-enabled"
								checked={canPushTracking}
								onCheckedChange={setCanPushTracking}
							/>
						</SettingRow>
						<SettingRow
							label="Allow public client (PKCE)"
							htmlFor="public-client-enabled"
							help="Lets browser and mobile builds of this app finish the connect flow with a PKCE verifier and receive an account-scoped grant token, with no app credential in the client. Only for apps that ship code to end users."
						>
							<Switch
								id="public-client-enabled"
								checked={allowPublicClient}
								onCheckedChange={setAllowPublicClient}
							/>
						</SettingRow>
					</PanelSettingRows>
				</DetailPanelSection>
			</DetailPanel>

			<div className="flex justify-end">
				<Button disabled={updatePrivileges.isPending} onClick={save}>
					{updatePrivileges.isPending ? "Saving…" : "Save"}
				</Button>
			</div>
		</div>
	)
}

/** Shows and changes the customer who owns the app. */
function OwnerCard({ app }: AppTabProps) {
	const setOwner = useSetIntegrationAppOwner()
	const [username, setUsername] = React.useState(app.ownerUsername ?? "")

	const current = app.ownerUsername ?? ""
	function save() {
		setOwner.mutate({ appId: app.id, ownerUsername: username.trim() || null })
	}

	return (
		<DetailPanel title="Owner" icon={UserRound}>
			<DetailPanelSection>
				<PanelSettingRows>
					<SettingRow
						label="Owner username"
						htmlFor="owner-username"
						help="The customer who manages this app: it appears in their CardNexus integration settings, where they rotate credentials, provision webhooks and view rate limits. Leave blank for an unowned, staff-managed app."
					>
						<div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
							<Input
								id="owner-username"
								value={username}
								onChange={(event) => setUsername(event.target.value)}
								placeholder="north_arena_tcg"
							/>
							<Button
								variant="outline"
								disabled={setOwner.isPending || username.trim() === current}
								onClick={save}
							>
								{setOwner.isPending ? "Saving…" : "Save owner"}
							</Button>
						</div>
						{app.ownerUsername ? (
							<p className="text-xs text-meta">
								Currently owned by @{app.ownerUsername}. Clear the field and
								save to remove.
							</p>
						) : null}
					</SettingRow>
				</PanelSettingRows>
			</DetailPanelSection>
		</DetailPanel>
	)
}

/** The app's per-account rate limits, with editable overrides. */
function RateLimitsTab({ appId }: { appId: string }) {
	const { buckets: effective, isLoading } = useIntegrationRateLimits(appId)
	const bucketsQuery = useRateLimitBuckets()
	const setRateLimits = useSetIntegrationRateLimits()

	const catalog = bucketsQuery.data?.buckets ?? []
	const [edits, setEdits] = React.useState<
		Record<string, { limit: string; windowSec: string }>
	>({})

	// Seed the editable fields from the effective table once it loads.
	React.useEffect(() => {
		if (effective.length === 0) return
		setEdits(
			Object.fromEntries(
				effective.map((b) => [
					b.name,
					{ limit: String(b.limit), windowSec: String(b.windowSec) },
				]),
			),
		)
	}, [effective])

	function setField(name: string, field: "limit" | "windowSec", value: string) {
		setEdits((prev) => ({
			...prev,
			[name]: { ...prev[name], [field]: value },
		}))
	}

	function resetRow(
		name: string,
		defaultLimit: number,
		defaultWindowSec: number,
	) {
		setField(name, "limit", String(defaultLimit))
		setField(name, "windowSec", String(defaultWindowSec))
	}

	function save() {
		const overrides: {
			bucketName: string
			limit: number
			windowSec: number
		}[] = []
		for (const b of catalog) {
			const edit = edits[b.name]
			if (!edit) continue
			const limit = Number.parseInt(edit.limit, 10)
			const windowSec = Number.parseInt(edit.windowSec, 10)
			if (!Number.isInteger(limit) || limit <= 0) continue
			if (!Number.isInteger(windowSec) || windowSec <= 0) continue
			// Only persist buckets that differ from the registry default.
			if (limit === b.defaultLimit && windowSec === b.defaultWindowSec) continue
			overrides.push({ bucketName: b.name, limit, windowSec })
		}
		setRateLimits.mutate({ appId, overrides })
	}

	if (isLoading || bucketsQuery.isLoading) {
		return <Skeleton className="h-64 w-full" />
	}

	return (
		<div className="flex flex-col gap-4">
			<DetailPanel
				title="Rate limits per connected account"
				icon={Gauge}
				meta="The owner sees these values read-only in their settings"
			>
				<div>
					<Table className="[&_td:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:first-child]:pl-4 [&_th:last-child]:pr-4">
						<TableHeader>
							<TableRow>
								<TableHead>Bucket</TableHead>
								<TableHead>Limit</TableHead>
								<TableHead className="text-right">Default</TableHead>
								<TableHead className="w-20" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{catalog.map((b) => {
								const edit = edits[b.name] ?? {
									limit: String(b.defaultLimit),
									windowSec: String(b.defaultWindowSec),
								}
								const isOverride =
									effective.find((e) => e.name === b.name)?.isOverride ?? false
								return (
									<TableRow key={b.name}>
										<TableCell>
											<span className="inline-flex items-center gap-2">
												<span className="font-mono text-xs">{b.name}</span>
												{isOverride && <Badge variant="info">custom</Badge>}
											</span>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-1.5">
												<Input
													aria-label={`${b.name} limit`}
													className="h-[26px] w-24 tabular-nums"
													value={edit.limit}
													onChange={(event) =>
														setField(b.name, "limit", event.target.value)
													}
												/>
												<span className="text-xs text-meta">per</span>
												<Input
													aria-label={`${b.name} window in seconds`}
													className="h-[26px] w-24 tabular-nums"
													value={edit.windowSec}
													onChange={(event) =>
														setField(b.name, "windowSec", event.target.value)
													}
												/>
												<span className="text-xs text-meta">s</span>
											</div>
										</TableCell>
										<TableCell className="text-right text-muted-foreground tabular-nums">
											{b.defaultLimit} / {b.defaultWindowSec}s
										</TableCell>
										<TableCell className="text-right">
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													resetRow(b.name, b.defaultLimit, b.defaultWindowSec)
												}
											>
												Reset
											</Button>
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>
					</Table>
				</div>
				<DetailPanelSection>
					<p className="text-xs text-meta">
						Each connected user gets this budget on the app's traffic. Leave a
						bucket at its default to use the standard limit.
					</p>
				</DetailPanelSection>
			</DetailPanel>
			<div className="flex justify-end">
				<Button disabled={setRateLimits.isPending} onClick={save}>
					{setRateLimits.isPending ? "Saving…" : "Save rate limits"}
				</Button>
			</div>
		</div>
	)
}
